"use client"

/**
 * QuestionComposerInspector — the right rail: Leo spotted, Properties
 * (Ready, locations, tags, discrimination). Item writing lives on the canvas.
 */

import * as React from "react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Tip } from "@/components/ui/tip"
import { QuestionQualityPane } from "@/components/question-quality/question-quality-pane"
import { QuestionQualityTile } from "@/components/question-quality/question-quality-tile"
import { ToggleSwitch } from "@/components/ui/toggle-switch"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form"
import { ComposerPanelCard } from "@/components/templates/composer-panel-card"
import {
  COMPOSER_FIELD_LABEL_CLASS,
  ComposerTagsField,
  DifficultyMeter,
  FolderPickerControl,
  InspectorSection,
} from "./composer-fields"
import {
  useQuestionComposerContext,
  useQuestionComposerInspectorSurface,
  useQuestionComposerPageScroll,
} from "./composer-context"

function CompactRailDot() {
  return (
    <span
      className="absolute -right-1 -top-1 size-1.5 rounded-full bg-destructive"
      aria-hidden="true"
    />
  )
}

export function QuestionInspectorCompactNav({
  density = "rail",
}: {
  density?: "rail" | "card"
}) {
  const { hasOpenReviewChecks, openLeoSpotted, openProperties } =
    useQuestionComposerContext()
  const rail = density === "rail"
  const buttonClass = rail
    ? "size-9 shrink-0 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
    : cn(
        "size-11 shrink-0 rounded-xl border-border shadow-sm",
        "bg-[var(--secondary-panel-bg)] text-sidebar-foreground",
        "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
      )

  const leoButton = (
    <Tip label="Leo AI" side="left">
      <Button
        type="button"
        size={rail ? "icon" : "icon-lg"}
        variant={rail ? "ghost" : "outline"}
        className={buttonClass}
        aria-label={
          hasOpenReviewChecks ? "Leo AI. Review checks are open." : "Leo AI"
        }
        onClick={openLeoSpotted}
      >
        <span className="relative">
          <i
            className="fa-duotone fa-solid fa-star-christmas text-md leading-none text-brand"
            aria-hidden="true"
          />
          {hasOpenReviewChecks ? <CompactRailDot /> : null}
        </span>
      </Button>
    </Tip>
  )

  const propertiesButton = (
    <Tip label="Properties" side="left">
      <Button
        type="button"
        size={rail ? "icon" : "icon-lg"}
        variant={rail ? "ghost" : "outline"}
        className={buttonClass}
        aria-label="Properties"
        onClick={openProperties}
      >
        <i className="fa-light fa-sliders text-md leading-none" aria-hidden="true" />
      </Button>
    </Tip>
  )

  if (rail) {
    return (
      <>
        <li className="flex w-full justify-center" role="none">
          {leoButton}
        </li>
        <li className="flex w-full justify-center" role="none">
          {propertiesButton}
        </li>
      </>
    )
  }

  return (
    <div className="flex w-full flex-col items-center gap-1 py-1">
      {leoButton}
      {propertiesButton}
    </div>
  )
}

export function QuestionComposerInspectorFields() {
  const {
    form,
    inspectorLead,
    disabled,
    folders,
    onRequestNewFolder,
    onReadyChange,
    onReplaceFromQuality,
    inspectorPanel,
    inspectorView,
    setInspectorView,
    focusCheckId,
    qualityChecks,
    qualityHistory,
    applyQualityCheck,
    dismissQualityCheck,
    enhanceWithLeo,
    openQuestionQuality,
    openProperties,
    folderPickerOpen,
    setFolderPickerOpen,
    difficultyInsight,
    tagDraft,
    setTagDraft,
    watchedTags,
    watchedStatus,
    commitTag,
    removeTag,
  } = useQuestionComposerContext()

  const isReady = watchedStatus === "in_review"

  if (inspectorPanel === "leo") {
    return (
      <fieldset disabled={disabled} className="contents">
        <div className="flex flex-col gap-6">
          {inspectorView !== "quality" ? (
            <QuestionQualityTile
              checks={qualityChecks}
              history={qualityHistory}
              disabled={disabled}
              onOpen={() => openQuestionQuality()}
            />
          ) : (
            <QuestionQualityPane
              checks={qualityChecks}
              history={qualityHistory}
              focusCheckId={focusCheckId}
              disabled={disabled}
              onBack={() => setInspectorView("settings")}
              onUse={applyQualityCheck}
              onDismiss={dismissQualityCheck}
              onEnhanceWithLeo={enhanceWithLeo}
              onReplace={onReplaceFromQuality}
              onRetagDifficulty={openProperties}
            />
          )}
        </div>
      </fieldset>
    )
  }

  return (
    <fieldset disabled={disabled} className="contents">
      <div className="flex flex-col gap-6">
        {onReadyChange ? (
          <div className="flex items-center justify-between gap-3 border-b border-border pb-4">
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <Label
                htmlFor="qb-mark-ready"
                className="text-sm font-medium text-foreground"
              >
                Question Ready
              </Label>
              <p className="text-xs leading-snug text-muted-foreground">
                {isReady
                  ? "Eligible for the assessment pool. Save keeps Ready."
                  : "Draft until you turn this on. Save writes the badge status."}
              </p>
            </div>
            <ToggleSwitch
              id="qb-mark-ready"
              checked={isReady}
              onChange={onReadyChange}
            />
          </div>
        ) : null}
        {inspectorLead}
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="folderIds"
              render={({ field }) => (
                <FormItem className="gap-1.5">
                  <Label className={COMPOSER_FIELD_LABEL_CLASS}>Locations</Label>
                  <FormControl>
                    <FolderPickerControl
                      folders={folders}
                      value={field.value}
                      onChange={(v) => field.onChange(v)}
                      open={folderPickerOpen}
                      onOpenChange={setFolderPickerOpen}
                      onRequestNewFolder={() => {
                        setFolderPickerOpen(false)
                        onRequestNewFolder?.()
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <InspectorSection title="Tags">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="qb-tag-input" className={COMPOSER_FIELD_LABEL_CLASS}>
                Tags
              </Label>
              <ComposerTagsField
                id="qb-tag-input"
                tags={watchedTags}
                draft={tagDraft}
                disabled={disabled}
                onDraftChange={setTagDraft}
                onCommit={commitTag}
                onRemove={removeTag}
              />
            </div>
          </InspectorSection>

          <InspectorSection title="Discrimination">
            <DifficultyMeter
              value={form.watch("difficulty")}
              onChange={() => undefined}
              insight={difficultyInsight}
              include={{ difficulty: false, bloom: false }}
            />
          </InspectorSection>
        </div>
      </div>
    </fieldset>
  )
}

export function QuestionComposerInspector() {
  const { form, inspectorOpen, setInspectorOpen, inspectorPanel } =
    useQuestionComposerContext()
  const pageScroll = useQuestionComposerPageScroll()
  const inspectorSurface = useQuestionComposerInspectorSurface()

  if (inspectorSurface === "secondary-panel") {
    return null
  }

  const hideInspectorButton = (
    <Tip side="bottom" label="Collapse to icons">
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={() => setInspectorOpen(false)}
        aria-label="Collapse to icons"
        aria-expanded={true}
      >
        <i className="fa-light fa-angles-right" aria-hidden="true" />
      </Button>
    </Tip>
  )

  return (
    <Form {...form}>
      {!inspectorOpen ? (
        <QuestionInspectorCompactNav density="card" />
      ) : (
        <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <ComposerPanelCard
            pageScrollBody={pageScroll}
            fillHeight
            title={inspectorPanel === "leo" ? "Leo spotted" : "Properties"}
            headerActions={hideInspectorButton}
          >
            <QuestionComposerInspectorFields />
          </ComposerPanelCard>
        </div>
      )}
    </Form>
  )
}
